import { ArrowLeftRight, Info } from 'lucide-react';
import TooltipWrapper, {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Table } from '@/components/ui/';
import { useMemo, useState } from 'react';
import {
  abbrFromSym,
  imgFromAbbr,
  symbolToBaseQuote,
} from '@/utils/token-symbol';
import { cn } from '@/utils/cn';
import { useOrders } from '../api/get-orders';
import {
  BigDecimal,
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
  formatDate,
  formatNumber,
  formatOrderStatus,
  formatOrderType,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { Order, useOrdersStore } from '@/store/use-orders-store';
import {
  ErrorCode,
  ErrorMsgs,
  OrderHistoryFilterType,
  OrderStatus,
  OrderType,
} from '@/types/enums';
import {
  COLLATERAL_DECIMALS,
  getAllMarketStoreStates,
  MarketSpec,
} from '@/store/use-markets-store';
import { useShallow } from 'zustand/react/shallow';
import { getOrderValueFromPrice } from '@/features/trade/order/utils/math';
import { Link } from 'react-router-dom';
import Share from '@/features/trade/components/share';
import { getOrderEntryPrice } from '../utils/math';
import { useUnifiedAccount } from '@/hooks';
import { ColumnDef } from '@tanstack/react-table';

type OrderHistory =
  | {
      id: string;
      status: OrderStatus.REJECTED | OrderStatus.CANCELLED;
      orderType: OrderType;
      symbol: string;
      side: boolean;
      size: bigint;
      value?: number;
      time: number;
      code?: ErrorCode;
      settledFunding: undefined;
      leverage: undefined;
      entryPrice: undefined;
      fees: undefined;
      lastFilledPrice: undefined;
      lastFilledSize: undefined;
      realizedPnl: undefined;
      roePct: undefined;
    }
  | {
      id: string;
      status: OrderStatus.FILLED;
      orderType: OrderType;
      symbol: string;
      side: boolean;
      size: bigint;
      value?: number;
      lastFilledSize: string;
      lastFilledPrice: number;
      settledFunding: bigint;
      fees: bigint;
      realizedPnl: number;
      time: number;
      leverage: string;
      roePct: bigint;
      entryPrice: string;
      code: undefined;
    };

const filterFn = (filter: OrderHistoryFilterType, order: Order) => {
  if (filter === OrderHistoryFilterType.ALL) {
    return true;
  } else if (filter === OrderHistoryFilterType.FILLED) {
    return order.status === OrderStatus.FILLED;
  } else if (filter === OrderHistoryFilterType.REJECTED) {
    return order.status === OrderStatus.REJECTED;
  } else if (filter === OrderHistoryFilterType.CANCELLED) {
    return order.status === OrderStatus.CANCELLED;
  }
};

type Props = {
  filter: OrderHistoryFilterType;
};

export default function OrdersHistoryTable({ filter }: Props) {
  const { address } = useUnifiedAccount();

  const ordersHistory = useOrdersStore(
    useShallow((state) => {
      const { filledOrderIds, cancelledOrderIds, rejectedOrderIds, orders } =
        state.getOrders(address);

      return Array.from(
        filledOrderIds.union(rejectedOrderIds).union(cancelledOrderIds),
        (id) => orders[id as string],
      ).filter(
        (order) =>
          order !== undefined &&
          (order.status === OrderStatus.FILLED || // TEMP
            order.status === OrderStatus.REJECTED ||
            order.status === OrderStatus.CANCELLED),
      );
    }),
  );

  const marketSpecs = useMemo(() => {
    const marketStores = getAllMarketStoreStates();
    return Object.entries(marketStores).reduce(
      (acc, [symbol, store]) => {
        acc[symbol] = store.marketSpec;
        return acc;
      },
      {} as Record<string, MarketSpec>,
    );
  }, []);

  const data: OrderHistory[] = useMemo(() => {
    return ordersHistory
      .filter(
        (order) =>
          marketSpecs[order.symbol] !== undefined && filterFn(filter, order),
      )
      .map((order) => {
        const marketSpec = marketSpecs[order.symbol];
        const orderValue = getOrderValueFromPrice(
          order.status === OrderStatus.FILLED
            ? order.lastFilledSize
            : order.size,
          order.status === OrderStatus.FILLED
            ? order.lastFilledPrice
            : order.limitPrice,
          marketSpec,
        );
        switch (order.status) {
          case OrderStatus.FILLED:
            const entryPrice = getOrderEntryPrice(order);
            const roePct =
              ((order.realizedPnl * 10n ** (COLLATERAL_DECIMALS + 4n)) /
                (order.initMarginRatio * order.size * entryPrice)) *
              100n;
            return {
              id: order.id,
              orderType: order.orderType,
              symbol: order.symbol,
              side: order.isBuy,
              entryPrice: bigIntToDecimalStr(
                entryPrice,
                marketSpec.priceDecimals,
              ),
              roePct,
              size: order.size,
              value: orderValue
                ? bigIntToNum(orderValue.bigint, COLLATERAL_DECIMALS)
                : undefined,
              lastFilledSize: bigIntToDecimalStr(
                order.lastFilledSize,
                marketSpec.sizeDecimals,
              ),
              lastFilledPrice: bigIntToNum(
                order.lastFilledPrice,
                marketSpec.priceDecimals,
              ),
              settledFunding: order.settledFunding,
              fees: order.fees,
              realizedPnl: bigIntToNum(order.realizedPnl, COLLATERAL_DECIMALS),
              leverage: formatBigInt(10n ** 6n / order.initMarginRatio, 2n, {
                digits: 0,
                round: 'ceil',
              }),
              status: order.status,
              time: order.lastFilledTime,
            };
          case OrderStatus.REJECTED:
            return {
              id: order.id,
              orderType: order.orderType,
              symbol: order.symbol,
              side: order.isBuy,
              size: order.size,
              value: orderValue
                ? bigIntToNum(orderValue.bigint, COLLATERAL_DECIMALS)
                : undefined,
              status: order.status,
              time: order.postTime,
              code: order.code,
            };
          case OrderStatus.CANCELLED:
            return {
              id: order.id,
              orderType: order.orderType,
              symbol: order.symbol,
              side: order.isBuy,
              size: order.size,
              value: orderValue
                ? bigIntToNum(orderValue.bigint, COLLATERAL_DECIMALS)
                : undefined,
              status: order.status,
              time: order.postTime,
            };
        }
      })
      .sort((a, b) => (b?.time ?? 0) - (a?.time ?? 0));
  }, [ordersHistory, marketSpecs, filter]);

  const columns = useMemo<ColumnDef<OrderHistory>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: (info) => (
          <div className="flex items-center gap-4">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger className="text-vestgrey-100">
                  <Info size={14} />
                </TooltipTrigger>
                <TooltipContent className="break-all">
                  {info.getValue() as string}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        header: 'ID',
        meta: { width: 'md:w-[5%] hidden md:table-cell' },
      },
      {
        accessorFn: ({ time }) =>
          formatDate(new Date(time).getTime(), {
            showTime: true,
            showSeconds: true,
          }),
        header: 'TIME',
        meta: { width: 'md:w-[15%] !pl-6 md:!pl-0' },
        sortingFn: (a, b) => a.original.time - b.original.time,
      },

      {
        accessorFn: ({ orderType }) => formatOrderType(orderType),
        header: 'TYPE',
        meta: { width: 'md:w-[7.5%] hidden md:table-cell' },
      },

      {
        accessorKey: 'symbol',
        cell: ({ row }) => (
          <Link
            to={`/trade/${row.original.symbol}`}
            className="flex items-center gap-3 font-sans sm:gap-4"
          >
            <img
              src={imgFromAbbr(abbrFromSym(row.original.symbol))}
              title={row.original.symbol}
              className="h-5 w-5"
            />
            <span className="hidden font-sans md:inline">
              {symbolToBaseQuote(row.original.symbol)}
            </span>
            <span className="font-sans md:hidden">
              {abbrFromSym(row.original.symbol)} /{' '}
              <span
                className={cn(
                  'font-mono',
                  row.original.side ? 'text-green' : 'text-red',
                )}
              >
                {row.original.side ? 'Long' : 'Short'}
              </span>
            </span>
          </Link>
        ),
        header: 'MARKET',
        meta: { width: 'md:w-[12.5%]' },
      },
      {
        accessorKey: 'side',
        cell: (info) => (
          <p className={cn(info.getValue() ? 'text-green' : 'text-red')}>
            {info.getValue() ? 'Long' : 'Short'}
          </p>
        ),
        header: 'SIDE',
        meta: { width: 'md:w-[5%] lg:w-[7.5%] hidden md:table-cell' },
      },
      {
        accessorKey: 'size',
        cell: ({ row }) =>
          `${bigIntToDecimalStr(
            row.original.size,
            marketSpecs[row.original.symbol].sizeDecimals,
          )} ${abbrFromSym(row.original.symbol)}`,
        header: 'SIZE',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
        sortDescFirst: false,
        sortingFn: (a, b) =>
          bigIntToNum(
            a.original.size,
            marketSpecs[a.original.symbol].sizeDecimals,
          ) -
          bigIntToNum(
            b.original.size,
            marketSpecs[b.original.symbol].sizeDecimals,
          ),
      },
      {
        accessorKey: 'value',
        cell: ({ row }) =>
          formatNumber(row.original.value, {
            digits: 2,
            round: 'ceil',
          }),
        header: 'VALUE',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorKey: 'lastFilledPrice',
        cell: ({ row }) =>
          row.original.lastFilledPrice === undefined
            ? '-'
            : formatNumber(row.original.lastFilledPrice),
        header: 'FILL PRICE',
        meta: { width: 'lg:w-[10%] hidden lg:table-cell' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorKey: 'settledFunding',
        cell: ({ row }) => (
          <span
            className={cn(
              row.original.settledFunding
                ? row.original.settledFunding < 0n
                  ? 'text-red'
                  : 'text-green'
                : 'text-vestgrey-100',
            )}
          >
            {row.original.status === OrderStatus.FILLED
              ? formatNumber(
                  bigIntToDecimalStr(
                    row.original.settledFunding,
                    COLLATERAL_DECIMALS,
                  ),
                  { digits: 2, showChange: true },
                )
              : '-'}
          </span>
        ),
        header: 'FUNDING',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorKey: 'fees',
        cell: ({ row }) => (
          <p
            className={cn(
              row.original.fees ? 'text-vestgrey' : 'text-vestgrey-100',
            )}
          >
            {row.original.status === OrderStatus.FILLED
              ? formatNumber(
                  bigIntToDecimalStr(row.original.fees, COLLATERAL_DECIMALS),
                  { digits: 2, round: 'ceil' },
                )
              : '-'}
          </p>
        ),
        header: 'FEE',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorKey: 'realizedPnl',
        cell: ({ row }) => {
          const {
            realizedPnl,
            roePct,
            status,
            symbol,
            lastFilledPrice,
            entryPrice,
            leverage,
            fees,
            side,
            settledFunding,
          } = row.original;
          return (
            <span
              className={cn(
                realizedPnl
                  ? realizedPnl < 0
                    ? 'text-red'
                    : 'text-green'
                  : 'text-vestgrey-100',
              )}
            >
              {/* <span className="mr-3 inline-block text-vestgrey-100 md:hidden">
                PNL
              </span> */}
              {status === OrderStatus.FILLED
                ? formatNumber(realizedPnl, { digits: 2, showChange: true })
                : '-'}
              {status === OrderStatus.FILLED &&
                parseDecimalToBigInt(
                  realizedPnl.toString(),
                  COLLATERAL_DECIMALS,
                ) !==
                  settledFunding - fees && (
                  <Share
                    symbol={symbol}
                    roePct={bigIntToDecimalStr(roePct, COLLATERAL_DECIMALS)}
                    leverage={leverage}
                    isLong={!side}
                    pnl={realizedPnl.toString()}
                    entryPrice={formatNumber(entryPrice)}
                    markPrice={formatNumber(lastFilledPrice)}
                    isFilled
                  />
                )}
            </span>
          );
        },

        header: () => (
          <div className="flex w-full max-w-max items-center justify-end md:justify-start">
            <TooltipWrapper
              offset={1.4}
              content="Includes fee and funding."
              className="font-sans text-base normal-case"
              direction="left"
            >
              <span className="md:underline">PNL</span>
            </TooltipWrapper>
          </div>
        ),
        meta: { width: 'md:w-[10%] !pr-6 md:!pr-0 text-right md:text-left' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => (
          <div className="flex items-center gap-3 sm:gap-4">
            {row.original.status ? formatOrderStatus(row.original.status) : '-'}
            {row.original.status === OrderStatus.REJECTED &&
              row.original.code && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <Info size={14} />
                    </TooltipTrigger>
                    <TooltipContent>
                      {ErrorMsgs[row.original.code]}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
          </div>
        ),
        header: 'STATUS',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
      },
    ],
    [],
  );

  const { isLoading } = useOrders();

  return (
    <Table
      data={data}
      columns={columns}
      // RowComponent={PositionRow}
      loading={isLoading}
      className="[&_th]:whitespace-nowrap"
    />
  );
}
